import axios from 'axios';
import { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { deepMerge } from '../../scripts/deepMerge';

const ensureTrailingSlash = (url: string): string => url.replace(/\/?$/, '/');

const removeSlash = (url: string): string => {
  return url.indexOf('/') === 0 ? url.substring(1) : url;
};

class HttpClient {
  private baseUrl: string;

  private middleware: ((
    config: AxiosRequestConfig,
  ) => AxiosRequestConfig | Promise<AxiosRequestConfig>)[] = [];

  private baseConfig: AxiosRequestConfig = {
    timeout: 20000,
    headers: {
      'country-Language': `${CSS_CONFIG.country}-${CSS_CONFIG.language}`,
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  };

  public constructor(baseUrl = '') {
    this.baseUrl = ensureTrailingSlash(baseUrl);
  }

  public applyMiddleware = async (
    initialConfig: AxiosRequestConfig = {},
  ): Promise<AxiosRequestConfig> =>
    this.middleware.reduce<Promise<AxiosRequestConfig>>(
      (config, applyChanges) => config.then(applyChanges),
      Promise.resolve(deepMerge(this.baseConfig, initialConfig)),
    );

  public get = async <T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.get(finalUrl, finalConfig);
  };

  public post = async <T, E>(
    url: string,
    body: E,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.post(finalUrl, body, finalConfig);
  };

  public put = async <T, E>(
    url: string,
    body: E,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.put(finalUrl, body, finalConfig);
  };

  public delete = async <T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    const finalConfig = await this.applyMiddleware(config);
    const finalUrl = this.baseUrl + removeSlash(url);
    return axios.delete(finalUrl, finalConfig);
  };
}

export default HttpClient;
